(function () {
  const assetVersion = '20260608-1335';
  const registryKey = '__sibrilleLightGradientComparison';
  const previewPrivacyStyleId = 'sibrille-preview-privacy-banner-fix';

  if (window[registryKey]) {
    window[registryKey].initAll(document);
    return;
  }

  const instances = new WeakMap();

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function normalizeDefault(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? clamp(parsed, 0, 100) : 70;
  }

  function fallbackAssetUrl(fileName) {
    const currentScript = document.currentScript;
    if (!currentScript || !currentScript.src) return fileName;
    return new URL(fileName, currentScript.src).href;
  }

  function prepareImage(image, fallbackFileName) {
    if (!image) return;

    image.loading = 'eager';
    image.decoding = 'async';
    image.fetchPriority = 'high';

    const fallbackSrc = image.dataset.fallbackSrc || fallbackAssetUrl(fallbackFileName);

    function useFallback() {
      if (!fallbackSrc || image.currentSrc.includes(fallbackSrc) || image.src.includes(fallbackSrc)) return;
      image.removeAttribute('srcset');
      image.src = fallbackSrc;
    }

    image.addEventListener('error', useFallback, { once: true });

    if (image.complete && !image.naturalWidth) {
      useFallback();
    }
  }

  function init(root) {
    if (!root) return;
    const media = root.querySelector('[data-sibrille-light-media]');
    const track = root.querySelector('[data-sibrille-light-track]');
    const handle = root.querySelector('[data-sibrille-light-handle]');
    const range = root.querySelector('[data-sibrille-light-range]');

    if (!media || !track || !handle) return;

    const existing = instances.get(root);
    if (existing) {
      if (existing.media === media && existing.track === track && existing.handle === handle) return;
      existing.destroy();
      instances.delete(root);
    }

    const controller = new AbortController();
    let dragController = null;
    let position = normalizeDefault(root.dataset.defaultPosition);
    const effect = root.dataset.sibrilleLightEffect || 'brightness';
    let brightnessFrames = [];
    let temperatureImages = [];

    root.classList.remove('is-native-range');
    root.classList.toggle('is-coordinate-drag', Boolean(range));

    if (effect === 'temperature') {
      temperatureImages = Array.from(media.querySelectorAll('[data-sibrille-temperature-image]'));
      temperatureImages.forEach((image) => {
        prepareImage(image, image.dataset.fallbackFile || 'sibrille-light-gradient-high-20260504.png');
      });
    } else {
      brightnessFrames = Array.from(media.querySelectorAll('[data-sibrille-brightness-frame]')).sort((a, b) => {
        return Number(a.dataset.sibrilleBrightnessFrame || 0) - Number(b.dataset.sibrilleBrightnessFrame || 0);
      });

      if (brightnessFrames.length) {
        brightnessFrames.forEach((image) => {
          prepareImage(image, image.dataset.fallbackFile || 'sibrille-light-gradient-low-20260504.png');
        });
      } else {
        const lowImage = media.querySelector('.sibrille-light-gradient__image--low');
        const highImage = media.querySelector('.sibrille-light-gradient__image--high');
        prepareImage(
          lowImage,
          (lowImage && lowImage.dataset.fallbackFile) || 'sibrille-light-gradient-low-20260504.png'
        );
        prepareImage(
          highImage,
          (highImage && highImage.dataset.fallbackFile) || 'sibrille-light-gradient-high-20260504.png'
        );
      }
    }

    function updateBrightnessLight(level) {
      const variant = root.dataset.sibrilleLightVariant || 'standard';
      const is72Torchiere = variant === '72-torchiere';
      const is36wBrightness = variant === '36w-rotating';
      const imageBrightness = is36wBrightness ? 0.55 + level * 0.63 : 0.78 + level * 0.27;
      const imageSaturation = is36wBrightness ? 0.82 + level * 0.18 : 0.92 + level * 0.08;
      const imageContrast = is36wBrightness ? 0.88 + level * 0.12 : 0.96 + level * 0.04;

      root.style.setProperty('--sibrille-demo-image-brightness', imageBrightness.toFixed(3));
      root.style.setProperty('--sibrille-demo-image-saturation', imageSaturation.toFixed(3));
      root.style.setProperty('--sibrille-demo-image-contrast', imageContrast.toFixed(3));

      if ((is72Torchiere || is36wBrightness) && brightnessFrames.length > 1) {
        const scaled = level * (brightnessFrames.length - 1);
        const lowerIndex = Math.min(brightnessFrames.length - 1, Math.floor(scaled));
        const upperIndex = Math.min(brightnessFrames.length - 1, lowerIndex + 1);
        const mix = Math.min(1, Math.max(0, scaled - lowerIndex));

        brightnessFrames.forEach((image, index) => {
          let opacity = 0;
          if (lowerIndex === upperIndex && index === lowerIndex) {
            opacity = 1;
          } else if (index === lowerIndex) {
            opacity = 1;
          } else if (index === upperIndex) {
            opacity = mix;
          }
          image.style.opacity = opacity.toFixed(4);
        });

        root.style.setProperty('--sibrille-demo-frame-index', scaled.toFixed(4));
      }
    }

    function updateTemperatureLight(level) {
      const variant = root.dataset.sibrilleLightVariant || 'standard';
      const isThreeHeadTemperature = variant === 'three-head';
      const is36wTemperature = variant === '36w-rotating';
      const is72TorchiereTemperature = variant === '72-torchiere';
      const usesTemperatureOverlay = isThreeHeadTemperature || is72TorchiereTemperature;
      const temperatureStops = isThreeHeadTemperature
        ? [2700, 4000, 5000, 6500]
        : is36wTemperature
          ? [3000, 4000, 5000, 6000]
          : [3000, 4000, 5000, 6500];
      const progress = clamp(level, 0, 1);
      const scaled = progress * (temperatureStops.length - 1);
      const lowerIndex = Math.min(temperatureStops.length - 1, Math.floor(scaled));
      const upperIndex = Math.min(lowerIndex + 1, temperatureStops.length - 1);
      const mix = upperIndex === lowerIndex ? 0 : Math.min(1, Math.max(0, scaled - lowerIndex));
      const opacities = [0, 0, 0, 0];
      const warmIntensity = usesTemperatureOverlay ? Math.max(0, 1 - level * 1.25) : 0;
      const neutralIntensity = usesTemperatureOverlay ? Math.max(0, 1 - Math.abs(level - 0.5) * 2) : 0;
      const coolIntensity = usesTemperatureOverlay ? Math.max(0, (level - 0.28) / 0.72) : 0;
      let imageSaturation = 1;
      let imageSepia = 0;
      let imageBrightness = 1;
      let imageContrast = 1;
      let imageHue = 0;
      let imageFilter = 'none';

      if (isThreeHeadTemperature) {
        imageSaturation = 1.025 - level * 0.035;
        imageSepia = Math.max(0, (1 - level) * 0.035);
        imageBrightness = 0.995 - level * 0.005;
      } else if (is72TorchiereTemperature) {
        imageSaturation = 1.02 + level * 0.08;
        imageSepia = Math.max(0, (1 - level) * 0.18);
        imageBrightness = 0.98 + level * 0.07;
        imageContrast = 0.98 + level * 0.05;
        imageHue = -10 + level * 20;
      }

      if (is36wTemperature) {
        // Keep the lower frame opaque so the media background never bleeds through during the fade.
        opacities[lowerIndex] = 1;
        if (upperIndex !== lowerIndex) {
          opacities[upperIndex] = mix;
        }
      } else if (level >= 1) {
        opacities[3] = 1;
      } else if (lowerIndex === 0) {
        if (isThreeHeadTemperature) {
          opacities[0] = (1 - mix) * 0.28;
          opacities[1] = 1;
        } else if (is72TorchiereTemperature) {
          opacities[0] = 1 - mix;
          opacities[1] = 1;
        } else {
          opacities[0] = 1 - mix;
          opacities[1] = mix;
        }
      } else if (lowerIndex === 1) {
        opacities[1] = 1;
        opacities[2] = mix;
      } else {
        opacities[2] = 1;
        opacities[3] = mix;
      }

      root.style.setProperty('--sibrille-temperature-3000-opacity', opacities[0].toFixed(4));
      root.style.setProperty('--sibrille-temperature-4000-opacity', opacities[1].toFixed(4));
      root.style.setProperty('--sibrille-temperature-5000-opacity', opacities[2].toFixed(4));
      root.style.setProperty('--sibrille-temperature-6500-opacity', opacities[3].toFixed(4));
      root.style.setProperty('--sibrille-temperature-warm-intensity', warmIntensity.toFixed(4));
      root.style.setProperty('--sibrille-temperature-neutral-intensity', neutralIntensity.toFixed(4));
      root.style.setProperty('--sibrille-temperature-cool-intensity', coolIntensity.toFixed(4));
      if (!is36wTemperature) {
        imageFilter = `saturate(${imageSaturation.toFixed(3)}) sepia(${imageSepia.toFixed(3)}) brightness(${imageBrightness.toFixed(3)}) contrast(${imageContrast.toFixed(3)}) hue-rotate(${imageHue.toFixed(2)}deg)`;
      }
      root.style.setProperty(
        '--sibrille-temperature-image-filter',
        imageFilter
      );

      if (is36wTemperature && temperatureImages.length) {
        temperatureImages.forEach((image, index) => {
          const isActiveNeighbor = index === lowerIndex || (index === upperIndex && mix > 0);
          image.style.opacity = (isActiveNeighbor ? opacities[index] : 0).toFixed(4);
          image.style.visibility = isActiveNeighbor ? 'visible' : 'hidden';
          image.style.zIndex = String(index === upperIndex && upperIndex !== lowerIndex ? 2 : 1);
        });
      }

      return Math.round(
        temperatureStops[lowerIndex] + (temperatureStops[upperIndex] - temperatureStops[lowerIndex]) * mix
      );
    }

    function setPosition(nextPosition, shouldSyncRange = true) {
      position = clamp(nextPosition, 0, 100);
      const value = `${position}%`;
      const level = position / 100;
      const inverseLevel = 1 - level;
      const glowOpacity = 0.05 + level * 0.72;
      const glowScale = 0.72 + level * 0.58;
      let temperature = Math.round(2700 + level * 3800);

      root.style.setProperty('--sibrille-light-position', value);
      root.style.setProperty('--sibrille-light-level', level.toFixed(4));
      root.style.setProperty('--sibrille-light-inverse-level', inverseLevel.toFixed(4));
      root.style.setProperty('--sibrille-light-warm-level', inverseLevel.toFixed(4));
      root.style.setProperty('--sibrille-light-cool-level', level.toFixed(4));
      root.style.setProperty('--sibrille-light-glow-opacity', glowOpacity.toFixed(4));
      root.style.setProperty('--sibrille-light-glow-scale', glowScale.toFixed(4));
      if (effect === 'temperature') {
        temperature = updateTemperatureLight(level);
      } else {
        updateBrightnessLight(level);
      }
      handle.setAttribute('aria-valuenow', String(Math.round(position)));
      handle.setAttribute(
        'aria-valuetext',
        effect === 'temperature' ? `${temperature}K color temperature` : `${Math.round(position)}% brightness`
      );
      if (shouldSyncRange && range && range.value !== String(Math.round(position))) {
        range.value = String(Math.round(position));
      }
    }

    function setRangeFromPosition(nextPosition) {
      if (!range) {
        setPosition(nextPosition);
        return;
      }

      const roundedPosition = String(Math.round(clamp(nextPosition, 0, 100)));
      if (range.value !== roundedPosition) {
        range.value = roundedPosition;
      }
      range.dispatchEvent(new Event('input', { bubbles: true }));
    }

    function positionFromClientX(clientX, sourceElement) {
      const rect = sourceElement.getBoundingClientRect();
      if (!rect.width) return position;
      return ((clientX - rect.left) / rect.width) * 100;
    }

    function clientXFromEvent(event) {
      if (typeof event.clientX === 'number') return event.clientX;
      const touch = event.touches && event.touches[0];
      const changedTouch = event.changedTouches && event.changedTouches[0];
      return touch ? touch.clientX : changedTouch ? changedTouch.clientX : position;
    }

    function clientYFromEvent(event) {
      if (typeof event.clientY === 'number') return event.clientY;
      const touch = event.touches && event.touches[0];
      const changedTouch = event.changedTouches && event.changedTouches[0];
      return touch ? touch.clientY : changedTouch ? changedTouch.clientY : NaN;
    }

    function startDrag(event, sourceElement, captureElement = sourceElement) {
      if (event.button !== undefined && event.button !== 0) return;

      event.preventDefault();
      root.classList.add('is-dragging');
      setRangeFromPosition(positionFromClientX(clientXFromEvent(event), sourceElement));

      if (captureElement.setPointerCapture && event.pointerId !== undefined) {
        try {
          captureElement.setPointerCapture(event.pointerId);
        } catch (error) {
          // Some embedded browsers reject pointer capture after synthetic events.
        }
      }

      if (dragController) dragController.abort();
      dragController = new AbortController();

      const options = { signal: dragController.signal };
      const dragMoveEvent = event.type === 'mousedown' ? 'mousemove' : event.type === 'touchstart' ? 'touchmove' : 'pointermove';
      const dragStopEvents =
        event.type === 'mousedown'
          ? ['mouseup']
          : event.type === 'touchstart'
            ? ['touchend', 'touchcancel']
            : ['pointerup', 'pointercancel'];

      function move(moveEvent) {
        moveEvent.preventDefault();
        setRangeFromPosition(positionFromClientX(clientXFromEvent(moveEvent), sourceElement));
      }

      function stop() {
        root.classList.remove('is-dragging');
        if (captureElement.releasePointerCapture && event.pointerId !== undefined) {
          try {
            captureElement.releasePointerCapture(event.pointerId);
          } catch (error) {
            // Pointer capture may already be released by the browser.
          }
        }
        if (dragController) {
          dragController.abort();
          dragController = null;
        }
      }

      window.addEventListener(dragMoveEvent, move, options);
      dragStopEvents.forEach((eventName) => window.addEventListener(eventName, stop, options));
    }

    function onKeydown(event) {
      let next = position;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          next -= 1;
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          next += 1;
          break;
        case 'PageDown':
          next -= 10;
          break;
        case 'PageUp':
          next += 10;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = 100;
          break;
        default:
          return;
      }

      event.preventDefault();
      setRangeFromPosition(next);
    }

    function onRangeInput(event) {
      const value = Number(event.currentTarget.value);
      if (!Number.isFinite(value)) return;
      root.classList.add('is-dragging');
      setPosition(value, false);
    }

    function onRangeRelease() {
      root.classList.remove('is-dragging');
      if (dragController) {
        dragController.abort();
        dragController = null;
      }
    }

    const downOptions = { signal: controller.signal };
    const touchDownOptions = { signal: controller.signal, passive: false };
    const bindDragStart = (element, sourceElement, captureElement = sourceElement) => {
      const start = (event) => {
        if (range && event.target === range) return;
        startDrag(event, sourceElement, captureElement);
      };
      element.addEventListener('pointerdown', start, downOptions);
      element.addEventListener('mousedown', start, downOptions);
      element.addEventListener('touchstart', start, touchDownOptions);
    };
    const bindHandleDragStart = () => {
      const start = (event) => {
        event.stopPropagation();
        startDrag(event, track, handle);
      };
      handle.addEventListener('pointerdown', start, downOptions);
      handle.addEventListener('mousedown', start, downOptions);
      handle.addEventListener('touchstart', start, touchDownOptions);
    };

    const isPointInTrackHotzone = (clientX, clientY) => {
      if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return false;
      const trackRect = track.getBoundingClientRect();
      const candidateRangeRect = range ? range.getBoundingClientRect() : trackRect;
      const rangeRect =
        candidateRangeRect.width && candidateRangeRect.height ? candidateRangeRect : trackRect;
      const left = Math.min(trackRect.left, rangeRect.left) - 32;
      const right = Math.max(trackRect.right, rangeRect.right) + 32;
      const top = Math.min(trackRect.top, rangeRect.top) - 40;
      const bottom = Math.max(trackRect.bottom, rangeRect.bottom) + 40;
      return clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
    };

    const bindPreviewOverlayRescue = () => {
      if (!isPreviewExperience()) return;

      const start = (event) => {
        if (root.contains(event.target)) return;
        if (!isShopifyPrivacyOverlayTarget(event.target)) return;

        const clientX = clientXFromEvent(event);
        const clientY = clientYFromEvent(event);
        if (!isPointInTrackHotzone(clientX, clientY)) return;

        event.stopPropagation();
        startDrag(event, track, document.documentElement);
      };

      document.addEventListener('pointerdown', start, { signal: controller.signal, capture: true });
      document.addEventListener('mousedown', start, { signal: controller.signal, capture: true });
      document.addEventListener('touchstart', start, { signal: controller.signal, capture: true, passive: false });
    };

    const bindCoordinateDragCapture = () => {
      let isCoordinateDragging = false;
      let activePointerId = null;

      const blockEvent = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      };

      const updateFromEvent = (event) => {
        setRangeFromPosition(positionFromClientX(clientXFromEvent(event), track));
      };

      const start = (event) => {
        if (event.button !== undefined && event.button !== 0) return;

        const clientX = clientXFromEvent(event);
        const clientY = clientYFromEvent(event);
        if (!isPointInTrackHotzone(clientX, clientY)) return;

        blockEvent(event);
        isCoordinateDragging = true;
        activePointerId = event.pointerId !== undefined ? event.pointerId : null;
        root.classList.add('is-dragging');
        updateFromEvent(event);
      };

      const move = (event) => {
        if (!isCoordinateDragging) return;
        if (activePointerId !== null && event.pointerId !== undefined && event.pointerId !== activePointerId) return;

        blockEvent(event);
        updateFromEvent(event);
      };

      const stop = (event) => {
        if (!isCoordinateDragging) return;
        if (activePointerId !== null && event.pointerId !== undefined && event.pointerId !== activePointerId) return;

        blockEvent(event);
        isCoordinateDragging = false;
        activePointerId = null;
        root.classList.remove('is-dragging');
      };

      document.addEventListener('pointerdown', start, { signal: controller.signal, capture: true });
      document.addEventListener('mousedown', start, { signal: controller.signal, capture: true });
      document.addEventListener('touchstart', start, { signal: controller.signal, capture: true, passive: false });
      document.addEventListener('pointermove', move, { signal: controller.signal, capture: true });
      document.addEventListener('mousemove', move, { signal: controller.signal, capture: true });
      document.addEventListener('touchmove', move, { signal: controller.signal, capture: true, passive: false });
      document.addEventListener('pointerup', stop, { signal: controller.signal, capture: true });
      document.addEventListener('pointercancel', stop, { signal: controller.signal, capture: true });
      document.addEventListener('mouseup', stop, { signal: controller.signal, capture: true });
      document.addEventListener('touchend', stop, { signal: controller.signal, capture: true });
      document.addEventListener('touchcancel', stop, { signal: controller.signal, capture: true });
    };

    bindPreviewOverlayRescue();
    bindCoordinateDragCapture();
    handle.addEventListener('keydown', onKeydown, { signal: controller.signal });
    if (range) {
      const stopRangeDragBubble = (event) => event.stopPropagation();
      range.addEventListener('pointerdown', stopRangeDragBubble, { signal: controller.signal });
      range.addEventListener('mousedown', stopRangeDragBubble, { signal: controller.signal });
      range.addEventListener('touchstart', stopRangeDragBubble, { signal: controller.signal, passive: true });
      range.addEventListener('input', onRangeInput, { signal: controller.signal });
      range.addEventListener('change', onRangeRelease, { signal: controller.signal });
      range.addEventListener('pointerup', onRangeRelease, { signal: controller.signal });
      range.addEventListener('mouseup', onRangeRelease, { signal: controller.signal });
      range.addEventListener('touchend', onRangeRelease, { signal: controller.signal });
      range.addEventListener('touchcancel', onRangeRelease, { signal: controller.signal });
      range.addEventListener('blur', onRangeRelease, { signal: controller.signal });
    } else {
      bindDragStart(media, media);
      bindDragStart(track, track);
      bindHandleDragStart();
    }

    setPosition(position);

    instances.set(root, {
      media,
      track,
      handle,
      destroy() {
        controller.abort();
        if (dragController) dragController.abort();
        root.classList.remove('is-dragging');
        root.classList.remove('is-native-range');
        root.classList.remove('is-coordinate-drag');
      },
    });
  }

  function initAll(scope) {
    const context = scope || document;
    context.querySelectorAll('[data-sibrille-light-root]').forEach(init);
  }

  function destroyAll(scope) {
    const context = scope || document;
    context.querySelectorAll('[data-sibrille-light-root]').forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  function hasShopifyPreviewBar() {
    return Array.from(document.querySelectorAll('iframe')).some((frame) => {
      const previewText = [
        frame.getAttribute('src') || '',
        frame.getAttribute('id') || '',
        frame.getAttribute('name') || '',
        frame.getAttribute('title') || '',
        frame.className || '',
      ]
        .join(' ')
        .toLowerCase();
      return (
        previewText.includes('shopifycloud/preview-bar') ||
        previewText.includes('/preview_bar') ||
        previewText.includes('preview-bar') ||
        previewText.includes('preview_bar')
      );
    });
  }

  function isPreviewExperience() {
    return (
      hasShopifyPreviewBar() ||
      Boolean(window.Shopify && window.Shopify.designMode) ||
      document.documentElement.classList.contains('shopify-design-mode') ||
      window.location.search.includes('preview_theme_id') ||
      document.cookie.includes('preview_theme_id') ||
      document.cookie.includes('shopify_theme_preview')
    );
  }

  function isShopifyPrivacyOverlayTarget(target) {
    const element = target && target.nodeType === 1 ? target : target && target.parentElement;
    if (!element || !element.closest) return false;
    return Boolean(
      element.closest(
        '#shopify-pc__banner, .shopify-pc__banner__dialog, [id^="shopify-pc__"], [class*="shopify-pc__"]'
      )
    );
  }

  function applyPreviewPrivacyBannerFix() {
    if (!isPreviewExperience()) return false;
    if (document.getElementById(previewPrivacyStyleId)) return true;

    const style = document.createElement('style');
    style.id = previewPrivacyStyleId;
    style.textContent = [
      '#shopify-pc__banner,',
      '.shopify-pc__banner__dialog,',
      '[id^="shopify-pc__banner"]{',
      'display:none!important;',
      'pointer-events:none!important;',
      '}',
    ].join('');
    document.head.appendChild(style);
    return true;
  }

  function watchPreviewPrivacyBanner() {
    if (applyPreviewPrivacyBannerFix() || !window.MutationObserver) return;

    const observer = new MutationObserver(() => {
      if (applyPreviewPrivacyBannerFix()) observer.disconnect();
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 15000);
  }

  window[registryKey] = {
    assetVersion,
    initAll,
    destroyAll,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  function scheduleDocumentInit(delay = 0) {
    window.setTimeout(() => initAll(document), delay);
  }

  window.addEventListener(
    'load',
    () => {
      initAll(document);
      [120, 500, 1500, 3000].forEach(scheduleDocumentInit);
    },
    { once: true }
  );

  [80, 300, 900, 1800].forEach(scheduleDocumentInit);

  watchPreviewPrivacyBanner();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
  document.addEventListener('shopify:section:select', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', (event) => initAll(event.target));

  if (window.MutationObserver) {
    let mutationRefresh = 0;
    const scheduleMutationInit = () => {
      window.clearTimeout(mutationRefresh);
      mutationRefresh = window.setTimeout(() => initAll(document), 80);
    };

    const observer = new MutationObserver((mutations) => {
      const hasLightDemoChange = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some((node) => {
          return (
            node.nodeType === 1 &&
            (node.matches?.('[data-sibrille-light-root], [data-sibrille-light-track], [data-sibrille-light-handle]') ||
              node.querySelector?.('[data-sibrille-light-root], [data-sibrille-light-track], [data-sibrille-light-handle]'))
          );
        });
      });

      if (hasLightDemoChange) scheduleMutationInit();
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
})();
